/**
 * Breathing protocols — the trauma-informed exercises.
 * Each protocol has introPhases (warm-up) and mainPhases (repeating cycle),
 * plus display metadata. Ported from the standalone Calm The Body app
 * (protocols.js).
 */

import { STATE_MAP } from './states';

export type PhaseType =
  | 'inhale'
  | 'top_up'
  | 'exhale'
  | 'hold_top'
  | 'hold_bottom'
  | 'rest';

export interface Phase {
  type: PhaseType;
  seconds: number;
  label: string;
  hint?: string;
}

export interface PreviewSection {
  label: string;
  phases: Phase[];
}

export interface SighIntro {
  title: string;
  subtitle: string;
  steps: string[];
  note?: string;
}

export interface Protocol {
  id: string;
  name: string;
  category: string;
  safetyLevel: string;
  tagline: string;
  reason: string;
  science: string;
  bestForStates: string[];
  introPhases: Phase[];
  mainPhases: Phase[];
  introText: string;
  previewSections?: PreviewSection[];
  sighIntro?: SighIntro;
  slowAnimation?: boolean;
  groundingFrequency?: number;
}

export const PROTOCOLS: Protocol[] = [
  {
    id: 'panic_reset',
    name: 'Panic Reset',
    category: 'exhale_focused',
    safetyLevel: 'gentle',
    tagline: 'Two settling sighs, then slower breathing',
    reason:
      'A double physiological sigh is the fastest known way to interrupt an acute stress spike. Two quick inhales fill the alveoli and maximise gas exchange; the extended exhale activates the vagal brake. After the spike is interrupted, a simple balanced rhythm prevents re-escalation.',
    science:
      'Balban et al. (2023, Cell Reports Medicine) found cyclic physiological sighing outperformed mindfulness meditation for real-time stress reduction. The double-inhale mechanism re-inflates collapsed alveoli; the long exhale shifts autonomic balance toward parasympathetic dominance via the Hering–Breuer reflex.',
    bestForStates: ['panic'],
    introPhases: [
      { type: 'inhale', seconds: 3, label: 'Breathe in', hint: 'gently through your nose' },
      { type: 'top_up', seconds: 2, label: 'Sip to fill your lungs', hint: '' },
      { type: 'hold_top', seconds: 1, label: 'Hold', hint: 'gently' },
      { type: 'exhale', seconds: 10, label: 'Very slow exhale through pursed lips', hint: 'all the way out' },
      { type: 'rest', seconds: 2, label: '', hint: '' },
      { type: 'inhale', seconds: 3, label: 'One more', hint: 'gently' },
      { type: 'top_up', seconds: 2, label: 'Sip to fill your lungs', hint: '' },
      { type: 'hold_top', seconds: 1, label: 'Hold', hint: '' },
      { type: 'exhale', seconds: 10, label: 'Very slow exhale through pursed lips', hint: 'slow and easy' },
    ],
    mainPhases: [
      { type: 'inhale', seconds: 5, label: 'Breathe in', hint: '' },
      { type: 'exhale', seconds: 5, label: 'Breathe out', hint: 'let it go' },
      { type: 'rest', seconds: 1, label: '', hint: '' },
    ],
    introText:
      "We'll start with two settling sighs to interrupt the spike, then ease into a steadier rhythm. Stop at any point if needed.",
    previewSections: [
      {
        label: 'Physiological sigh (×2)',
        phases: [
          { type: 'inhale', seconds: 3, label: 'Breathe in through nose' },
          { type: 'top_up', seconds: 2, label: 'Sip to fill lungs' },
          { type: 'hold_top', seconds: 1, label: 'Hold gently' },
          { type: 'exhale', seconds: 10, label: 'Very slow exhale' },
        ],
      },
      {
        label: 'Settling rhythm',
        phases: [
          { type: 'inhale', seconds: 5, label: 'Breathe in' },
          { type: 'exhale', seconds: 5, label: 'Breathe out' },
        ],
      },
    ],
    sighIntro: {
      title: "We'll begin with two physiological sighs",
      subtitle: 'The fastest way to interrupt a panic spike',
      steps: [
        'Breathe in gently through your nose',
        'Then sip in a little more air to fill your lungs completely',
        'Hold for just a moment',
        'Let it all out, very slowly through pursed lips',
      ],
      note: "We'll guide you through this twice, then ease into a steadier rhythm.",
    },
  },
  {
    id: 'find_the_ground',
    name: 'Find the Ground',
    category: 'balanced',
    safetyLevel: 'gentle',
    tagline: 'Equal rhythm to restore a sense of control',
    reason:
      'Overwhelm floods the system; everything feels like too much at once. An equal-ratio breath gives the nervous system a predictable rhythm to latch onto. The sameness of each phase replaces chaos with pattern, signalling safety through structure.',
    science:
      'Equal-ratio breathing at 5-5-5-5 produces HRV coherence, a measurable state where heart rate, respiration and blood pressure synchronise. Post-exhale hold maximises vagal tone.',
    bestForStates: ['overwhelm'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 5, label: 'Breathe in', hint: '' },
      { type: 'hold_top', seconds: 5, label: 'Hold', hint: 'gently, no strain' },
      { type: 'exhale', seconds: 5, label: 'Breathe out', hint: '' },
      { type: 'hold_bottom', seconds: 5, label: 'Hold', hint: '' },
    ],
    introText:
      'Follow the rhythm. Each phase is the same length. Let that sameness be the anchor.',
  },
  {
    id: 'relax_box',
    name: 'Box Breathing',
    category: 'balanced',
    safetyLevel: 'gentle',
    tagline: 'Slow, even rhythm to settle the body',
    reason:
      "When you don't need rescuing, you just need to soften. Box breathing at 4-4-4-4 is short enough to feel easy on a hard day, and even enough to drop the body out of low-grade activation. Predictable, undemanding, kind.",
    science:
      'Even-ratio paced breathing around six breaths per minute increases vagal tone and HRV coherence. The mild post-exhale hold sustains the parasympathetic shift without effort.',
    bestForStates: ['relax'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 4, label: 'Breathe in', hint: 'gently' },
      { type: 'hold_top', seconds: 4, label: 'Hold', hint: 'no strain' },
      { type: 'exhale', seconds: 4, label: 'Breathe out', hint: 'let it go' },
      { type: 'hold_bottom', seconds: 4, label: 'Hold', hint: '' },
    ],
    introText: 'A soft, even rhythm. Nothing to push, just follow the box.',
  },
  {
    id: 'long_let_go',
    name: 'Long Let-Go',
    category: 'exhale_focused',
    safetyLevel: 'gentle',
    tagline: 'Twice as long out as in',
    reason:
      'When you feel wired or on edge, your inhale dominates: quick, shallow breaths that keep you locked in sympathetic activation. Doubling the exhale flips the ratio and engages the vagal brake without requiring stillness or silence.',
    science:
      'The Hering–Breuer reflex triggers parasympathetic signalling when lungs deflate slowly. Additionally, the extended exhale phase increases baroreceptor stimulation in the aortic arch, activating the vagal brake.',
    bestForStates: ['edgy'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 4, label: 'Breathe in', hint: '' },
      { type: 'hold_top', seconds: 2, label: 'Hold', hint: '' },
      { type: 'exhale', seconds: 8, label: 'Breathe out', hint: 'twice as long' },
      { type: 'hold_bottom', seconds: 2, label: 'Hold', hint: '' },
    ],
    introText:
      "Let the long exhale do the work. You don't need to force the breath out, just let it fall.",
  },
  {
    id: 'loop_breaker',
    name: 'The Loop Breaker',
    category: 'balanced',
    safetyLevel: 'gentle',
    tagline: 'Continuous rhythm to counter the mental loop',
    reason:
      'Looping thoughts need a counter-rhythm, not an instruction to stop. A continuous, unbroken 5–5 breath gives the mind something physical to track and gradually draws attention out of the narrative loop.',
    science:
      'Unbroken 5–5 rhythm at ~0.1Hz is the resonance frequency of the cardiovascular system, and it maximises heart rate variability and synchronises respiratory sinus arrhythmia (RSA). The effortless technique is deliberate: cognitive simplicity reduces the mental load that feeds rumination.',
    bestForStates: ['rumination'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 5, label: 'Breathe in', hint: '' },
      { type: 'exhale', seconds: 5, label: 'Breathe out', hint: '' },
    ],
    introText:
      "Just follow the rhythm. Thoughts may still come, and that's fine. The breath is the anchor, not the absence of thought.",
  },
  {
    id: 'sleep_descent',
    name: 'Sleep Descent',
    category: 'exhale_focused',
    safetyLevel: 'gentle',
    tagline: 'Slow the system toward rest',
    reason:
      "Racing thoughts at night are a sign your nervous system hasn't downshifted. This pattern uses a long hold and slow exhale to bring your breath rate well below resting, signalling the body it's safe to let go.",
    science:
      'Post-inhale hold raises CO2 tolerance and reduces chemoreceptor sensitivity, both of which lower respiratory drive. The extended exhale phase engages vagal outflow. The counting required to track phases occupies prefrontal cortex resources, disrupting the default mode network that drives rumination.',
    bestForStates: ['cant_sleep'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 4, label: 'Breathe in', hint: 'through your nose' },
      { type: 'hold_top', seconds: 7, label: 'Hold', hint: 'gently, no force needed' },
      { type: 'exhale', seconds: 8, label: 'Breathe out', hint: 'slowly through your mouth' },
      { type: 'rest', seconds: 2, label: '', hint: '' },
    ],
    introText:
      "Do this lying down if you can. Start with just a few cycles. If the hold feels like too much, breathe out early, and that's completely fine.",
  },
  {
    id: 'come_back',
    name: 'Come Back',
    category: 'grounding',
    safetyLevel: 'gentle',
    tagline: 'Gentle lift to bring you back online',
    reason:
      'Dissociation is a dorsal vagal shutdown, where your system has decided the safest option is to check out. This pattern gently reverses that by favouring the inhale, nudging toward ventral vagal via the mobilisation pathway. Extended exhale protocols should be avoided in this state, since they deepen shutdown rather than resolve it. Pair with physical grounding (feet flat on floor, cold water on wrists) to support re-embodiment.',
    science:
      'Dorsal vagal shutdown requires careful upregulation, not deeper settling. An inhale-dominant ratio gently activates the sympathetic system while the short exhale prevents over-activation.',
    bestForStates: ['dissociated'],
    slowAnimation: true,
    groundingFrequency: 3,
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 6, label: 'Breathe in', hint: 'feel the breath fill you' },
      { type: 'hold_top', seconds: 3, label: 'Hold', hint: '' },
      { type: 'exhale', seconds: 4, label: 'Breathe out', hint: '' },
      { type: 'rest', seconds: 1, label: '', hint: '' },
    ],
    introText:
      "Nothing to force here. If the breath doesn't feel comfortable, try pressing your feet gently into the floor and let that be enough.",
  },
  {
    id: 'voo_breath',
    name: 'The Voo Breath',
    category: 'grounding',
    safetyLevel: 'gentle',
    tagline: 'Use sound to vibrate the vagus nerve directly',
    reason:
      'When you feel emotionally flat or unreachable, silent breathing often isn\'t enough. Making the "voo" sound on the exhale creates a vibration that directly stimulates the vagus nerve and asks the body to start engaging again. Humming or sighing works as a gentler alternative.',
    science:
      "The vagal nerve innervates the larynx, so vocal vibration on the exhale directly stimulates vagal tone while the act of producing sound requires active physiological engagement. An equal inhale-to-exhale ratio is used deliberately here (not exhale-dominant) because the goal is gentle mobilisation, not further settling. Peter Levine's somatic approach underpins this technique.",
    bestForStates: ['flat'],
    introPhases: [],
    mainPhases: [
      { type: 'inhale', seconds: 4, label: 'Breathe in', hint: '' },
      { type: 'hold_top', seconds: 2, label: 'Hold', hint: '' },
      { type: 'exhale', seconds: 4, label: 'Breathe out', hint: 'make a low "voo" sound' },
      { type: 'rest', seconds: 1, label: '', hint: '' },
    ],
    introText:
      'On the exhale, make a low, sustained "voooo" sound through your open mouth. Feel it vibrate in your chest. Humming works too if that feels easier.',
  },
];

export const PROTOCOL_MAP: Record<string, Protocol> = Object.fromEntries(
  PROTOCOLS.map((p) => [p.id, p]),
);

/** Look up a protocol by id. */
export function getProtocol(id: string): Protocol | undefined {
  return PROTOCOL_MAP[id];
}

/**
 * Resolve the primary protocol for a given state id.
 * Returns the first primary protocol that actually exists in PROTOCOL_MAP.
 */
export function protocolForState(stateId: string): Protocol | undefined {
  const state = STATE_MAP[stateId];
  if (!state) return undefined;
  for (const pid of state.primaryProtocolIds) {
    const proto = PROTOCOL_MAP[pid];
    if (proto) return proto;
  }
  return undefined;
}
